import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PageShell } from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Briefcase, Plus, Loader2, Users, Eye, Pencil } from "lucide-react";

const STATUS_STYLE = {
  DRAFT: "bg-slate-100 text-slate-600", PUBLISHED: "bg-emerald-50 text-emerald-700",
  CLOSED: "bg-amber-50 text-amber-700", ARCHIVED: "bg-slate-100 text-slate-400",
};

export default function MyRecruitment() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);

  const load = () => api.get("/me/recruitment").then((r) => setItems(r.data || [])).catch((e) => { toast.error(apiError(e)); setItems([]); });
  useEffect(() => { load(); }, []);

  const setStatus = async (rid, action) => {
    try { await api.patch(`/recruitment/${rid}/status`, { action }); toast.success("Updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <PageShell>
      <div className="max-w-4xl mx-auto" data-testid="my-recruitment">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-2xl font-bold text-[#0F2C59]">My Recruitment</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage your opportunities and applicants.</p>
          </div>
          <Button data-testid="my-recruitment-new" onClick={() => nav("/post-recruitment")} className="rounded-xl bg-[#0F2C59] hover:bg-[#0F2C59]/90"><Plus className="h-4 w-4 mr-1.5" /> Post</Button>
        </div>

        {items === null ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div data-testid="my-recruitment-empty" className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 py-16 text-center">
            <Briefcase className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-slate-500">You haven't posted any opportunities yet.</p>
            <Button onClick={() => nav("/post-recruitment")} className="mt-5 rounded-xl bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white"><Plus className="h-4 w-4 mr-1.5" /> Post your first opportunity</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((r) => (
              <div key={r.id} data-testid={`my-recruitment-${r.id}`} className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-start sm:items-center gap-2 min-w-0">
                    <span className="font-semibold text-[#0F2C59] line-clamp-2 sm:truncate">{r.title}</span>
                    <span className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status] || "bg-slate-100"}`}>{r.status}</span>
                  </div>
                  <div className="text-[13px] text-slate-500 mt-0.5">{r.employment_type_label}{r.city ? ` · ${r.city}` : ""} · {r.application_count || 0} applicant{(r.application_count || 0) === 1 ? "" : "s"}</div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap sm:flex-nowrap sm:shrink-0">
                  <Link to={`/recruitment/${r.id}/applicants`}><Button data-testid={`recruitment-applicants-${r.id}`} variant="outline" size="sm" className="rounded-xl"><Users className="h-4 w-4 mr-1" /> Applicants</Button></Link>
                  <Link to={`/recruitment/${r.id}`}><Button variant="ghost" size="icon" className="rounded-xl"><Eye className="h-4 w-4" /></Button></Link>
                  <Link to={`/post-recruitment/${r.id}/edit`}><Button variant="ghost" size="icon" className="rounded-xl"><Pencil className="h-4 w-4" /></Button></Link>
                  {r.status === "PUBLISHED" ? (
                    <Button data-testid={`recruitment-close-${r.id}`} onClick={() => setStatus(r.id, "close")} variant="ghost" size="sm" className="rounded-xl text-amber-700">Close</Button>
                  ) : r.status === "DRAFT" ? (
                    <Button data-testid={`recruitment-publish-${r.id}`} onClick={() => setStatus(r.id, "publish")} variant="ghost" size="sm" className="rounded-xl text-emerald-700">Publish</Button>
                  ) : r.status === "CLOSED" ? (
                    <Button onClick={() => setStatus(r.id, "publish")} variant="ghost" size="sm" className="rounded-xl text-emerald-700">Reopen</Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
}
